import { ContentState, convertFromRaw, EditorState } from 'draft-js';
import { Opportunity, QLog, QNote, QTask } from '@qwealth/qdata';
import { getFile } from '@qwealth/qvault';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { MeetingNote } from 'data/models/meetingNote';
import { isJsonString } from 'data/helpers/data';
import { buildEditorStateFromString } from 'data/helpers/editorState';

export const buildNoteContent = async (
  note?: QNote | MeetingNote | Partial<QTask> | QLog | Opportunity | null,
): Promise<EditorState> => {
  let noteState: EditorState = EditorState.createEmpty();
  // @ts-ignore
  const s3Attachments = note?.s3Attachments || [];
  const noteContent =
    (note as QNote)?.content ??
    (note as MeetingNote)?.meetingNote ??
    (note as QTask)?.details ??
    (note as QLog)?.content ??
    (note as Opportunity)?.description;

  try {
    if (noteContent) {
      if (isJsonString(noteContent)) {
        noteState = EditorState.createWithContent(convertFromRaw(JSON.parse(noteContent)));
      } else {
        if (note && 'noteContent' in note) {
          noteState = buildEditorStateFromString(`<p>${noteContent}</p>`);
        } else {
          noteState = EditorState.createWithContent(ContentState.createFromText(noteContent));
        }
      }
    } else if (s3Attachments.length === 1) {
      const hasRtfAttached = s3Attachments[0].key.toLowerCase().includes('.rtf');
      if (hasRtfAttached) {
        noteState = await getFile(s3Attachments[0].key)
          .then(blob => blob?.text())
          .then(rawRtf => (rawRtf ? buildEditorStateFromString(rawRtf) : EditorState.createEmpty()))
          .catch(err => {
            console.error(err);
            return EditorState.createEmpty();
          });
      }
    }
  } catch (error) {
    console.error('Could not parse editor state', error);
  }

  return noteState;
};

export const useInitializedEditorState = (
  note?: QNote | MeetingNote | Partial<QTask> | QLog | Opportunity | null,
): [EditorState, Dispatch<SetStateAction<EditorState>>] => {
  const [editorState, setEditorState] = useState<EditorState>(EditorState.createEmpty());

  useEffect(() => {
    buildNoteContent(note).then(initialState => {
      setEditorState(initialState);
    });
  }, [note, setEditorState]);

  return [editorState, setEditorState];
};
